import React, { useState, useRef } from 'react';
import { Camera, Video, Mic, X, Upload, Check } from 'lucide-react';
import { contributeMedia } from '../../../services/mediaService';

function ContributeToStory({ storyId, storyTitle, onContributed }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const accepted = [];
    const rejected = [];

    files.forEach(file => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const isAudio = file.type.startsWith('audio/');

      if (!isImage && !isVideo && !isAudio) {
        rejected.push(`"${file.name}" — unsupported file type`);
        return;
      }

      const maxMB = isVideo ? 500 : isAudio ? 50 : 10;
      if (file.size > maxMB * 1024 * 1024) {
        const label = isVideo ? 'Videos' : isAudio ? 'Audio files' : 'Images';
        rejected.push(`"${file.name}" is too large (${(file.size/(1024*1024)).toFixed(0)} MB). ${label} must be under ${maxMB} MB.`);
        return;
      }

      accepted.push(file);
    });

    if (rejected.length > 0) {
      setError(rejected.join('\n'));
    }

    // Create preview URLs
    const filesWithPreviews = accepted.map(file => ({
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      type: file.type.startsWith('image/') ? 'image' :
            file.type.startsWith('video/') ? 'video' : 'audio'
    }));

    setSelectedFiles(prev => [...prev, ...filesWithPreviews]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => {
      const updated = [...prev];
      if (updated[index].preview) {
        URL.revokeObjectURL(updated[index].preview);
      }
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const files = selectedFiles.map(f => f.file);
      const result = await contributeMedia(storyId, files, { onProgress: (pct) => setUploadProgress(pct) });

      setSuccess(true);
      setSelectedFiles([]);

      // Notify parent to refresh
      onContributed?.(result);

      // Close modal after a short delay
      setTimeout(() => {
        setIsModalOpen(false);
        setSuccess(false);
      }, 1500);

    } catch (err) {
      console.error('Error contributing media:', err);
      setError(err.message || 'Failed to upload. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const openFilePicker = (accept) => {
    if (fileInputRef.current) {
      fileInputRef.current.accept = accept;
      fileInputRef.current.click();
    }
  };

  const closeModal = () => {
    // Clean up previews
    selectedFiles.forEach(f => {
      if (f.preview) URL.revokeObjectURL(f.preview);
    });
    setSelectedFiles([]);
    setError(null);
    setSuccess(false);
    setIsModalOpen(false);
  };

  return (
    <>
      {/* CTA Button */}
      <div className="sidebar-card contribute-cta">
        <div className="cta-header">
          <span className="cta-icon">📸</span>
          <h3 className="cta-title">Add to This Story</h3>
        </div>
        <p className="cta-description">
          Have photos, videos, or recordings related to this memory? Add them here!
        </p>
        <button
          className="cta-button contribute-button"
          onClick={() => setIsModalOpen(true)}
        >
          <Upload size={16} />
          <span>Contribute Media</span>
        </button>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="contribute-modal-overlay" onClick={closeModal}>
          <div className="contribute-modal" onClick={e => e.stopPropagation()}>
            <div className="contribute-modal-header">
              <h2>Add to "{storyTitle}"</h2>
              <button className="modal-close-btn" onClick={closeModal}>
                <X size={20} />
              </button>
            </div>

            <div className="contribute-modal-body">
              {success ? (
                <div className="contribute-success">
                  <div className="success-icon">
                    <Check size={48} />
                  </div>
                  <h3>Thank you!</h3>
                  <p>Your contribution has been added to the story.</p>
                </div>
              ) : (
                <>
                  <p className="contribute-intro">
                    Share your own photos, videos, or audio recordings to help tell this story from your perspective.
                  </p>

                  {/* Error Message */}
                  {error && (
                    <div className="contribute-error">
                      {error}
                      <button onClick={() => setError(null)}><X size={14} /></button>
                    </div>
                  )}

                  {/* Upload Options */}
                  <div className="contribute-options">
                    <button
                      className="contribute-option"
                      onClick={() => openFilePicker('image/*')}
                      disabled={uploading}
                    >
                      <Camera size={24} />
                      <span>Photos</span>
                    </button>
                    <button
                      className="contribute-option"
                      onClick={() => openFilePicker('video/*')}
                      disabled={uploading}
                    >
                      <Video size={24} />
                      <span>Videos</span>
                    </button>
                    <button
                      className="contribute-option"
                      onClick={() => openFilePicker('audio/*')}
                      disabled={uploading}
                    >
                      <Mic size={24} />
                      <span>Audio</span>
                    </button>
                  </div>

                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />

                  {/* Selected Files Preview */}
                  {selectedFiles.length > 0 && (
                    <div className="contribute-preview">
                      <h4>Selected Files ({selectedFiles.length})</h4>
                      <div className="preview-grid">
                        {selectedFiles.map((item, index) => (
                          <div key={index} className="preview-item">
                            {item.type === 'image' && item.preview ? (
                              <img src={item.preview} alt={item.file.name} />
                            ) : (
                              <div className="preview-placeholder">
                                {item.type === 'video' ? <Video size={24} /> : <Mic size={24} />}
                              </div>
                            )}
                            <button
                              className="preview-remove"
                              onClick={() => removeFile(index)}
                            >
                              <X size={14} />
                            </button>
                            <span className="preview-name">{item.file.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {!success && (
              <div className="contribute-modal-footer">
                {uploading && (
                  <div style={{ width: '100%', marginBottom: 10 }}>
                    <div style={{ background: '#EFEEE5', borderRadius: 999, height: 6, overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 999, background: uploadProgress < 100 ? '#556B2F' : '#2F7A3A', width: `${uploadProgress}%`, transition: 'width 0.3s' }} />
                    </div>
                    <p style={{ textAlign: 'center', fontSize: 12, color: '#556B2F', fontWeight: 600, marginTop: 4 }}>
                      {uploadProgress < 100 ? `Uploading... ${uploadProgress}%` : 'Processing...'}
                    </p>
                  </div>
                )}
                <button className="btn-cancel" onClick={closeModal} disabled={uploading}>
                  Cancel
                </button>
                <button
                  className="btn-submit"
                  onClick={handleSubmit}
                  disabled={selectedFiles.length === 0 || uploading}
                >
                  {uploading ? `Uploading... ${uploadProgress}%` : `Add ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default ContributeToStory;
